/**
 * Cartesian product math utilities.
 *
 * All functions are pure and DOM-free. No Math.random().
 * Order is a-major: all pairs/tuples with a[0] first, then a[1], etc.
 */

/**
 * Return the Cartesian product A × B.
 * Elements are ordered a-major: for each element of `a`, pair it with every
 * element of `b` in order, before moving to the next element of `a`.
 */
export function product<A, B>(a: readonly A[], b: readonly B[]): [A, B][] {
  const result: [A, B][] = [];
  for (const x of a) {
    for (const y of b) {
      result.push([x, y]);
    }
  }
  return result;
}

/**
 * Return the Cartesian triple A × B × C.
 * Ordered a-major, then b, then c: iterate a outermost, c innermost.
 */
export function triple<A, B, C>(a: readonly A[], b: readonly B[], c: readonly C[]): [A, B, C][] {
  const result: [A, B, C][] = [];
  for (const x of a) {
    for (const y of b) {
      for (const z of c) {
        result.push([x, y, z]);
      }
    }
  }
  return result;
}

/**
 * Return the n-fold Cartesian power A^n.
 * Each element is an n-tuple (represented as an array).
 * `power(a, 0)` returns `[[]]` (one empty tuple).
 * `power(a, 1)` returns singleton arrays `[[a[0]], [a[1]], …]`.
 * Length is `|a|^n`.
 */
export function power<A>(a: readonly A[], n: number): A[][] {
  if (n === 0) {
    return [[]];
  }
  const result: A[][] = [];
  const tails = power(a, n - 1);
  for (const x of a) {
    for (const tail of tails) {
      result.push([x, ...tail]);
    }
  }
  return result;
}
